import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';

const openMenus = new Set<() => void>();

/** Fecha todos os ActionMenu abertos (ex.: antes de abrir um Dialog). */
export function closeAllActionMenus() {
  openMenus.forEach((close) => close());
}

interface ActionMenuProps {
  children: React.ReactNode;
  trigger?: React.ReactNode;
  align?: 'end' | 'start';
  /** Sobrescreve tamanho/estilo do botão que abre o menu (ex.: igual aos ícones do cabeçalho do jogo). */
  triggerButtonClassName?: string;
  triggerVariant?: React.ComponentProps<typeof Button>['variant'];
  triggerTitle?: string;
}

export function ActionMenu({
  children,
  trigger,
  align = 'end',
  triggerButtonClassName,
  triggerVariant = 'ghost',
  triggerTitle,
}: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    openMenus.add(closeMenu);
    return () => {
      openMenus.delete(closeMenu);
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) closeMenu();
    };
    
    // Update position on scroll to avoid detached menu
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) && 
        triggerRef.current && 
        !triggerRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    const handleDialogOpen = () => {
      if (document.querySelector('[role="dialog"][data-state="open"]')) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      handleDialogOpen();
      const observer = new MutationObserver(handleDialogOpen);
      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-state'],
      });
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
        observer.disconnect();
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  const toggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const style: React.CSSProperties =
        align === 'end'
          ? {
              position: 'fixed',
              top: rect.bottom + 4,
              right: window.innerWidth - rect.right,
              zIndex: 40,
            }
          : {
              position: 'fixed',
              top: rect.bottom + 4,
              left: rect.left,
              zIndex: 40,
            };

      setMenuStyle(style);
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant={triggerVariant}
        size="icon"
        className={cn('h-8 w-8 p-0', triggerButtonClassName)}
        onClick={toggleOpen}
        title={triggerTitle}
      >
        {trigger || <Icon name="more_vert" className="text-[20px] text-muted-foreground" />}
        <span className="sr-only">{triggerTitle ?? 'Abrir menu'}</span>
      </Button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="min-w-[8rem] overflow-hidden rounded-xl border bg-popover p-1 text-popover-foreground shadow-lg"
          onClick={closeMenu}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
}

export function ActionMenuItem({
  children,
  onClick,
  className,
  destructive = false,
  disabled = false,
  icon,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  destructive?: boolean;
  disabled?: boolean;
  /** Nome do ícone (ex: "replay", "delete") */
  icon?: string;
}) {
  return (
    <div
      onClick={() => {
        if (disabled) return;
        onClick?.();
      }}
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-lg px-2 py-2 text-sm !font-normal outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        destructive && "text-destructive focus:text-destructive hover:bg-destructive/10",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {icon && <Icon name={icon} className="text-[18px] shrink-0" />}
      {children}
    </div>
  );
}

export function ActionMenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
      {children}
    </div>
  );
}

export function ActionMenuSeparator() {
  return (
    <div className="-mx-1 my-1 h-px bg-muted" />
  );
}
